import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { isValidEmail } from '../../utils/formValidation';
import './style.less';

/**
 * Newsletter subscription form component
 * @param {string} variant - 'footer' | 'popup' - affects layout
 * @param {Function} onSuccess - callback after successful subscription
 */
export const NewsletterForm = ({ variant = 'footer', onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error' | 'duplicate'

  const validateForm = () => {
    if (!isValidEmail(email)) {
      setError(t('newsletter.errors.emailInvalid'));
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
    }
    // Reset status when user starts typing again
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // In development mode, show info message instead of making API call
    if (process.env.NODE_ENV === 'development') {
      setSubmitStatus('devMode');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/newsletter-subscribe.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source: variant,
          language: i18n.language,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setEmail('');
        localStorage.setItem('newsletter_subscribed', 'true');
        onSuccess?.();
      } else if (result.error === 'already_subscribed') {
        setSubmitStatus('duplicate');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (submitStatus) {
      case 'success':
        return 'fa-check-circle';
      case 'duplicate':
      case 'devMode':
        return 'fa-info-circle';
      case 'error':
        return 'fa-exclamation-circle';
      default:
        return '';
    }
  };

  const getStatusMessage = () => {
    switch (submitStatus) {
      case 'success':
        return t('newsletter.success');
      case 'duplicate':
        return t('newsletter.duplicate');
      case 'devMode':
        return t('newsletter.devMode');
      case 'error':
        return t('newsletter.error');
      default:
        return '';
    }
  };

  return (
    <form
      className={`newsletter-form newsletter-form--${variant}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="newsletter-form__input-group">
        <div className="newsletter-form__input-wrapper">
          <input
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            placeholder={t('newsletter.form.emailPlaceholder')}
            className={`newsletter-form__input ${error ? 'newsletter-form__input--error' : ''}`}
            disabled={isSubmitting || submitStatus === 'success'}
            aria-label={t('newsletter.form.email')}
            aria-invalid={!!error}
            aria-describedby={error ? 'newsletter-error' : undefined}
          />
          {error && (
            <span id="newsletter-error" className="newsletter-form__error" role="alert">
              {error}
            </span>
          )}
        </div>

        <button
          type="submit"
          className="newsletter-form__submit"
          disabled={isSubmitting || submitStatus === 'success'}
          aria-label={isSubmitting ? t('newsletter.form.submitting') : t('newsletter.form.submit')}
        >
          {isSubmitting ? (
            <>
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
              <span>{t('newsletter.form.submitting')}</span>
            </>
          ) : (
            <>
              <i className="fas fa-envelope" aria-hidden="true"></i>
              <span>{t('newsletter.form.submit')}</span>
            </>
          )}
        </button>
      </div>

      <p className="newsletter-form__privacy-note">
        <Trans i18nKey="newsletter.privacyNote" components={{ 1: <Link to="/privacy-policy" /> }} />
      </p>

      <AnimatePresence mode="wait">
        {submitStatus && (
          <motion.div
            className={`newsletter-form__status newsletter-form__status--${submitStatus}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            role="status"
            aria-live="polite"
          >
            <i className={`fas ${getStatusIcon()}`} aria-hidden="true"></i>
            <span>{getStatusMessage()}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
};
